import type { ReactElement } from 'react';
import { useState } from 'react';
import { Copy, RefreshCw } from 'lucide-react';
import { api } from '../api';

interface Props {
  spaceId: string;
  inviteCode: string | null;
  inviteCodeGeneratedAt: string | null;
  inviteCodeUsedAt: string | null;
  onRegenerated?: (newCode: string) => void;
}

export function InviteCodeDisplay({
  spaceId,
  inviteCode,
  inviteCodeGeneratedAt,
  inviteCodeUsedAt,
  onRegenerated,
}: Props): ReactElement {
  const [regenerating, setRegenerating] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [code, setCode] = useState<string | null>(inviteCode);
  const [error, setError] = useState<string | null>(null);

  const handleCopy = (): void => {
    if (code) void navigator.clipboard.writeText(code);
  };

  const handleRegenerate = async (): Promise<void> => {
    setRegenerating(true);
    setError(null);
    try {
      const result = await api.regenerateInviteCode(spaceId);
      setCode(result.invite_code);
      setShowConfirm(false);
      onRegenerated?.(result.invite_code);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'regenerate failed');
    } finally {
      setRegenerating(false);
    }
  };

  const handleGenerate = async (): Promise<void> => {
    setRegenerating(true);
    setError(null);
    try {
      const result = await api.regenerateInviteCode(spaceId);
      setCode(result.invite_code);
      onRegenerated?.(result.invite_code);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'generate failed');
    } finally {
      setRegenerating(false);
    }
  };

  if (!code) {
    return (
      <div className="space-y-2">
        <label className="block text-xs text-slate-400">Invite Code</label>
        <button
          type="button"
          onClick={() => void handleGenerate()}
          disabled={regenerating}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-accent text-slate-900 text-sm font-medium disabled:opacity-50 hover:bg-accent/90 transition-colors"
        >
          <RefreshCw size={14} className={regenerating ? 'animate-spin' : ''} />
          Generate Invite Code
        </button>
        {error && <p className="text-rose-400 text-xs">{error}</p>}
        <p className="text-xs text-slate-500">
          Generate a 6-digit code that the UE operator enters at runtime to
          bind their client to this Space.
        </p>
      </div>
    );
  }

  return (
    <section className="space-y-2">
      <label className="block text-xs text-slate-400">Invite Code</label>
      <div className="flex items-center gap-3">
        <span className="font-mono text-3xl font-bold tracking-[0.3em] text-accent select-all">
          {code}
        </span>
        <button
          type="button"
          onClick={handleCopy}
          className="p-1.5 rounded bg-slate-800 text-slate-300 hover:text-accent transition-colors"
          title="Copy invite code"
        >
          <Copy size={16} />
        </button>
      </div>

      <div className="text-xs text-slate-500 space-y-0.5">
        {inviteCodeGeneratedAt && (
          <div>
            Generated:{' '}
            {new Date(inviteCodeGeneratedAt).toLocaleString()}
          </div>
        )}
        {inviteCodeUsedAt ? (
          <div className="text-emerald-400">
            Bound at: {new Date(inviteCodeUsedAt).toLocaleString()}
          </div>
        ) : (
          <div className="text-amber-400">Never bound (unbound)</div>
        )}
      </div>

      <p className="text-xs text-slate-500">
        Give this code to the UE operator. They enter it at runtime in the
        GimbalProxy plugin to bind the UE client to this Space.
      </p>

      {error && <p className="text-rose-400 text-xs">{error}</p>}

      {!showConfirm ? (
        <button
          type="button"
          onClick={() => setShowConfirm(true)}
          disabled={regenerating}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-700 text-amber-100 text-xs disabled:opacity-50"
        >
          <RefreshCw size={12} className={regenerating ? 'animate-spin' : ''} />
          Regenerate
        </button>
      ) : (
        <div className="flex items-center gap-2">
          <span className="text-xs text-amber-400">
            This invalidates the current code. Already-bound UE instances
            using their stored auth token are unaffected. Continue?
          </span>
          <button
            type="button"
            onClick={() => void handleRegenerate()}
            disabled={regenerating}
            className="px-2 py-0.5 rounded bg-amber-600 text-amber-100 text-xs font-medium disabled:opacity-50"
          >
            Yes
          </button>
          <button
            type="button"
            onClick={() => setShowConfirm(false)}
            className="px-2 py-0.5 rounded bg-slate-700 text-xs"
          >
            Cancel
          </button>
        </div>
      )}
    </section>
  );
}
